import React, { useState } from 'react';
import { Typography, FormControl, Select, MenuItem, Button, Stack, SelectChangeEvent, Box, CircularProgress, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from '@mui/icons-material';
import orderService, { type UpdateOrderItemServiceResult } from 'api/orderService';
import useAuth from 'hooks/useAuth';
import type { SerializedSaveQueueStatus } from 'hooks/useSerializedSaveQueue';
import OnHold from './OnHold';
import CancelOrder from './CancelOrder';
import CompleteOrder from './CompleteOrder';
import { UploadedFile } from './Questions';
import MainCard from './MainCard';
import { statusChips } from 'data/OrderItemStatus';
import { getLockOwnerName } from 'utils/orderLocking';

// Define a default status option value that won't match any real status
const DEFAULT_STATUS_OPTION = 'select_status';

interface UpdateOrderItemStatusProps {
  orderId: string;
  isFormValid: boolean;
  onStatusUpdate: (newStatus: string, comment?: string | null) => void;
  isHistory?: boolean;
  isQc?: boolean;
  selectForQc: () => boolean;
  uploadedFiles?: UploadedFile[];
  onFilesUploaded?: () => void;
  onSave?: () => Promise<void> | void;
  saveStatus?: SerializedSaveQueueStatus;
  editLeaseId: string | null;
  onLockConflict?: (lockOwnerName: string) => void;
  onReleaseEditLease?: () => Promise<void> | void;
}

interface UpdateOrderItemStatusData {
  reason: string;
  comment: string;
}

interface OrderItemStatusUpdate {
  status?: string;
  completedBy?: string;
  qualityControlledBy?: string;
}

const UpdateOrderItemStatus: React.FC<UpdateOrderItemStatusProps> = ({
  orderId,
  isFormValid,
  onStatusUpdate,
  isHistory,
  isQc,
  selectForQc,
  onSave,
  saveStatus,
  editLeaseId,
  onLockConflict,
  onReleaseEditLease
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(DEFAULT_STATUS_OPTION);
  const [openOnHoldDialog, setOpenOnHoldDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const { user } = useAuth();
  const isSaveInProgress = saveStatus === 'saving';
  const hasEditLease = !!editLeaseId;
  const isStatusSelectDisabled = isHistory || isSaveInProgress || !hasEditLease;
  const isCompleteDisabled = !isFormValid || isSaveInProgress || !hasEditLease;

  const updateOrderItem = (updates: OrderItemStatusUpdate) => {
    if (!editLeaseId) {
      throw new Error('Cannot update order item status without an edit lease.');
    }

    return orderService.updateOrderItem(orderId, updates, undefined, editLeaseId);
  };

  const handleLockConflictResult = (result: UpdateOrderItemServiceResult): boolean => {
    if (!result.lockOwnerName) return false;

    onLockConflict?.(result.lockOwnerName);
    return true;
  };

  const handleLockConflictError = (error: any): boolean => {
    const lockOwnerName = getLockOwnerName(error);

    if (!lockOwnerName) return false;

    onLockConflict?.(lockOwnerName);
    return true;
  };

  const postStatusWithComment = (statusId: string, commentText: string) => {
    if (!editLeaseId) {
      throw new Error('Cannot update order item status without an edit lease.');
    }

    return orderService.postStatusWithComment(orderId, user?.userId!, statusId, commentText, editLeaseId);
  };

  const saveAnswersBeforeStatusChange = async (): Promise<boolean> => {
    if (!onSave) return true;

    try {
      await onSave();
      return true;
    } catch (error) {
      console.error('Failed to save answers before status update:', error);
      return false;
    }
  };

  const handleChange = async (event: SelectChangeEvent<string>) => {
    if (isStatusSelectDisabled) {
      return;
    }

    const newStatus = event.target.value;

    // Handle the default option - just update the UI dropdown but don't change the actual status
    if (newStatus === DEFAULT_STATUS_OPTION) {
      setStatus(DEFAULT_STATUS_OPTION);
      return;
    }

    try {
      switch (newStatus) {
        case 'On hold':
          setOpenOnHoldDialog(true);
          break;
        case 'Cancelled':
          setOpenCancelDialog(true);
          break;
        case 'QC fail':
          await setOrderStatusToQcFail();
          break;
        default:
          if (!(await saveAnswersBeforeStatusChange())) {
            return;
          }

          const updateResult = await updateOrderItem({ status: newStatus });
          if (handleLockConflictResult(updateResult)) {
            return;
          }

          setStatus(newStatus);
          onStatusUpdate(newStatus);
          setStatus(DEFAULT_STATUS_OPTION);
      }
    } catch (error) {
      if (handleLockConflictError(error)) {
        return;
      }

      console.error('Failed to update status:', error);
    }
  };

  const handleCloseOnHoldDialog = () => {
    setOpenOnHoldDialog(false);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  const handleCloseCompleteDialog = () => {
    setOpenCompleteDialog(false);
  };

  const updateStatus = async (status: string, data: UpdateOrderItemStatusData): Promise<boolean> => {
    try {
      const newComment = data.comment ? `${data.reason} (${data.comment})` : data.reason;
      const newStatusId = statusChips[status]?.id ?? null;

      if (!(await saveAnswersBeforeStatusChange())) {
        return false;
      }

      const commentId = await postStatusWithComment(newStatusId, newComment);

      // Reset the dropdown back to "Select Status" after successful update
      if (commentId && onStatusUpdate) {
        if (status === 'Cancelled') {
          try {
            await onReleaseEditLease?.();
          } catch (error) {
            console.error('Failed to release edit lease after cancellation:', error);
          }
        }

        setStatus(DEFAULT_STATUS_OPTION);
        onStatusUpdate(status, newComment);
        return true;
      }

      return false;
    } catch (error) {
      if (handleLockConflictError(error)) {
        return false;
      }

      console.error('Failed to update status:', error);
      return false;
    }
  };

  const handleConfirmOnHold = async (data: UpdateOrderItemStatusData) => {
    return await updateStatus('On hold', data);
  };

  const handleConfirmCancel = async (data: UpdateOrderItemStatusData) => {
    return await updateStatus('Cancelled', data);
  };

  const completeOrder = async (): Promise<boolean> => {
    if (!(await saveAnswersBeforeStatusChange())) {
      return false;
    }

    const status = selectForQc() ? 'QC' : 'Complete';
    const update = isQc ? { status, qualityControlledBy: user?.userId } : { status, completedBy: user?.userId };
    const updateResult = await updateOrderItem(update);
    return !handleLockConflictResult(updateResult);
  };

  const handleConfirmComplete = async () => {
    try {
      const didCompleteOrder = await completeOrder();
      if (didCompleteOrder) {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to complete order:', error);
    }
  };

  const setOrderStatusToQcFail = async (): Promise<boolean> => {
    if (!(await saveAnswersBeforeStatusChange())) {
      return false;
    }

    const updateResult = await updateOrderItem({ status: 'QC fail', qualityControlledBy: user?.userId });
    if (handleLockConflictResult(updateResult)) {
      return false;
    }

    navigate('/');
    return true;
  };

  const handleSave = async () => {
    if (isHistory || isSaveInProgress || !hasEditLease || !onSave) return;
    try {
      await onSave();
    } catch (error) {
      console.error('Failed to save order:', error);
    }
  };

  const handleComplete = () => {
    if (isCompleteDisabled) {
      return;
    }

    setOpenCompleteDialog(true);
  };

  const renderSaveStatus = () => {
    switch (saveStatus) {
      case 'dirty':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, color: 'warning.main' }}>
            <Typography variant="body2">Unsaved changes</Typography>
          </Box>
        );
      case 'saving':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, color: 'text.secondary' }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2">Saving</Typography>
          </Box>
        );
      case 'saved':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, color: 'success.main' }}>
            <CheckCircle fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">Saved</Typography>
          </Box>
        );
      case 'failed':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, color: 'error.main' }}>
            <Typography variant="body2">Save failed</Typography>
          </Box>
        );
      case 'conflicted':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, color: 'warning.main' }}>
            <Typography variant="body2">Save conflict</Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Grid item xs={12} md={12} sx={{ alignSelf: 'flex-start' }}>
        <MainCard title="Update Status" sx={{ mb: 3 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <Select
                value={status}
                onChange={handleChange}
                displayEmpty
                disabled={isStatusSelectDisabled}
                sx={{
                  fontSize: '0.875rem',
                  borderRadius: 0,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  }
                }}
              >
                <MenuItem value={DEFAULT_STATUS_OPTION} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  Select Status
                </MenuItem>
                <MenuItem value="In progress">In-Progress</MenuItem>
                <MenuItem value="On hold">On Hold</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                {isQc && <MenuItem value="QC fail">QC Fail</MenuItem>}
              </Select>
            </FormControl>

            {!isHistory && (
              <>
                <Stack direction="row" alignItems="center">
                  {renderSaveStatus()}
                  <Button
                    variant="outlined"
                    onClick={handleSave}
                    disabled={isSaveInProgress || !hasEditLease}
                    sx={{
                      textTransform: 'none',
                      color: 'text.primary',
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      borderRadius: 0,
                      '&:hover': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: 'text.primary'
                      },
                      '&.Mui-disabled': {
                        opacity: 0.7
                      },
                      mr: 2
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    className="complete-button"
                    variant="contained"
                    onClick={handleComplete}
                    disabled={isCompleteDisabled}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: isCompleteDisabled ? '#cccccc' : '#008E9C',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: isCompleteDisabled ? '#cccccc' : '#01707a'
                      },
                      '&.Mui-disabled': {
                        color: '#ffffff',
                        opacity: 0.7,
                        cursor: 'not-allowed'
                      },
                      '&::after': {
                        boxShadow: 'none !important'
                      }
                    }}
                  >
                    Complete
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </MainCard>
      </Grid>

      {!isHistory && (
        <>
          <OnHold open={openOnHoldDialog} onClose={handleCloseOnHoldDialog} onConfirm={handleConfirmOnHold} />
          <CancelOrder isOpen={openCancelDialog} onClose={handleCloseCancelDialog} orderId={orderId} onConfirm={handleConfirmCancel} />
          <CompleteOrder isOpen={openCompleteDialog} onClose={handleCloseCompleteDialog} onConfirm={handleConfirmComplete} />
        </>
      )}
    </>
  );
};

export default UpdateOrderItemStatus;
